import MarkdownIt from "markdown-it";

// PERF-01: highlight.js is the largest dependency, so it loads on first use
// (its own chunk in production). Until then code renders as escaped text and
// is highlighted in place once the grammar arrives.
let hljs;
let loading;
export function loadHighlighter() {
  loading ??= import("highlight.js/lib/common").then((module) => {
    hljs = module.default;
    rendered.clear();
    return hljs;
  });
  return loading;
}
const highlightable = (code, language) =>
  // Keep huge blocks readable without running a grammar on the UI thread.
  code.length <= 100_000 && language && hljs?.getLanguage(language);
const markdown = new MarkdownIt({
  html: false,
  breaks: true,
  highlight(code, language) {
    if (!highlightable(code, language)) return "";
    return hljs.highlight(code, { language, ignoreIllegals: true }).value;
  },
});
markdown.disable("image");
const linkOpen = markdown.renderer.rules.link_open;
markdown.renderer.rules.link_open = (tokens, index, options, env, renderer) => {
  tokens[index].attrSet("target", "_blank");
  tokens[index].attrSet("rel", "noopener noreferrer");
  return linkOpen
    ? linkOpen(tokens, index, options, env, renderer)
    : renderer.renderToken(tokens, index, options);
};

const rendered = new Map();
export function renderMarkdown(content) {
  if (rendered.has(content)) return rendered.get(content);
  const html = markdown.render(content);
  // Polling rebuilds the timeline; do not parse/highlight unchanged messages again.
  if (rendered.size >= 100) rendered.delete(rendered.keys().next().value);
  rendered.set(content, html);
  return html;
}

export async function copyText(text) {
  if (globalThis.navigator?.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return;
    } catch {
      // The LAN HTTP deployment and denied clipboard permission need a fallback.
    }
  }
  const active = document.activeElement;
  const selection = document.getSelection();
  const ranges = selection
    ? Array.from({ length: selection.rangeCount }, (_, index) =>
        selection.getRangeAt(index).cloneRange(),
      )
    : [];
  const buffer = document.createElement("textarea");
  buffer.className = "copy-buffer";
  buffer.value = text;
  buffer.readOnly = true;
  buffer.setAttribute("aria-label", "Copy buffer");
  document.body.append(buffer);
  try {
    buffer.select();
    buffer.setSelectionRange(0, text.length);
    if (!document.execCommand("copy")) throw new Error("Clipboard unavailable");
  } finally {
    buffer.remove();
    active?.focus({ preventScroll: true });
    if (selection) {
      selection.removeAllRanges();
      for (const range of ranges) selection.addRange(range);
    }
  }
}

export function createCopyButton(text, label = "Copy message") {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "ui-control copy-button";
  button.title = label;
  button.setAttribute("aria-label", label);
  const icon = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  icon.setAttribute("viewBox", "0 0 24 24");
  icon.setAttribute("aria-hidden", "true");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", "M9 9h11v11H9zM5 15H4V4h11v1");
  icon.append(path);
  const status = document.createElement("span");
  status.textContent = "Copy";
  status.setAttribute("aria-live", "polite");
  button.append(icon, status);
  button.addEventListener("click", async () => {
    button.disabled = true;
    try {
      await copyText(text);
      status.textContent = "Copied";
      button.setAttribute("aria-label", `${label}: copied`);
    } catch {
      status.textContent = "Couldn't copy";
      button.setAttribute(
        "aria-label",
        `${label}: failed, select text to copy`,
      );
    } finally {
      button.disabled = false;
      button.focus({ preventScroll: true });
    }
  });
  button.addEventListener("blur", () => {
    status.textContent = "Copy";
    button.setAttribute("aria-label", label);
  });
  return button;
}

export function renderAssistantContent(content) {
  const template = document.createElement("template");
  // Only the HTML-disabled Markdown parser and escaping highlighter produce HTML.
  template.innerHTML = renderMarkdown(content);
  const pending = [];
  for (const pre of template.content.querySelectorAll("pre")) {
    const code = pre.querySelector("code");
    if (!code) continue;
    const block = document.createElement("div");
    block.className = "code-block";
    const header = document.createElement("div");
    header.className = "code-block-header";
    const language = document.createElement("span");
    language.textContent =
      code.className.match(/(?:^|\s)language-(\S+)/)?.[1] || "text";
    header.append(language, createCopyButton(code.textContent, "Copy code"));
    pre.tabIndex = 0;
    pre.setAttribute("aria-label", `${language.textContent} code`);
    pre.before(block);
    block.append(header, pre);
    if (!hljs && language.textContent !== "text") pending.push(code);
  }
  if (pending.length)
    void loadHighlighter()
      .then(() => {
        for (const code of pending) {
          const language = code.className.match(/(?:^|\s)language-(\S+)/)?.[1];
          const text = code.textContent;
          if (!highlightable(text, language)) continue;
          code.innerHTML = hljs.highlight(text, {
            language,
            ignoreIllegals: true,
          }).value;
        }
      })
      .catch(() => {});
  for (const table of template.content.querySelectorAll("table")) {
    const scroll = document.createElement("div");
    scroll.className = "markdown-table";
    scroll.tabIndex = 0;
    scroll.setAttribute("role", "region");
    scroll.setAttribute("aria-label", "Message table");
    table.before(scroll);
    scroll.append(table);
  }
  return template.content;
}
